<script lang="ts">

  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import { Button } from "$lib/components/ui/button"
  import { Input } from "$lib/components/ui/input"
  import type { _response } from "$lib/data/type/_response"
  import { createSearch } from "$lib/hooks/search.svelte"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import ArrowLeftIcon from "@lucide/svelte/icons/arrow-left"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import RefreshCwIcon from "@lucide/svelte/icons/refresh-cw"
  import { createInfiniteQuery } from "@tanstack/svelte-query"
  import DialogDetail from "./_components/dialog-detail.svelte"
  import type { Staff } from "./_types"

  const prefix = $derived(page.params.prefix ?? "0")

  let sentinel = $state<HTMLDivElement>()

  let selected = $state<Staff>()
  const detailDialog = useDialog()

  let search = createSearch()
  const query = createInfiniteQuery(() => ({
    queryKey: ["staff", {
      prefix,
      search: search.debounced
    }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {

      const response = await httpClient.get<_response<Staff[]>>(`/api/${prefix}/staffs`, {
        params: {
          page: pageParam,
          search: search.debounced
        },
      })

      return response.data.result ?? []
    },
    getNextPageParam: (last, pages) => (last.length === 0 ? undefined : pages.length),
  }))

  const rows = $derived(query.data?.pages.flat() ?? [])


  $effect(() => {
    if (!sentinel || !query.hasNextPage || query.isFetching) return

    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) void query.fetchNextPage()
    })

    observer.observe(sentinel)
    return () => observer.disconnect()
  })

</script>

<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/home`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full"
          aria-label="Kembali">
          <ArrowLeftIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">Data Sales</h1>
      </div>
      <div></div>
    </div>
    <div class="flex gap-2 px-3 py-2">
      <Button
        variant="outline"
        size="icon"
        class="group"
        data-loading={query.isFetching}
        disabled={query.isFetching}
        onclick={() => query.refetch()}>
        <LoaderCircleIcon
          class="invisible absolute animate-spin group-data-[loading=true]:visible"
          aria-hidden="true"/>
        <RefreshCwIcon
          class="group-data-[loading=true]:invisible"
          aria-hidden="true"/>
      </Button>
      <Input
        class="flex-1"
        placeholder="Cari..."
        value={search.state}
        oninput={(e) => search.set(e.currentTarget.value)}/>
    </div>
  </nav>
  <div class="min-h-full">
    {#if query.isFetching && !rows.length}
      <div class="p-4 text-center text-xs">Memuat...</div>
    {:else if !rows.length}
      <div class="p-4 text-center text-xs">Tidak ada data</div>
    {:else}
      {#each rows as staff (staff.id)}
        <Button
          variant="ghost"
          class="block h-auto w-full justify-start px-3 py-2 text-left text-sm hover:bg-muted"
          onclick={() => {
            selected = staff
            detailDialog.setOpen(true)
          }}>
          <div class="w-full">
            <div class="font-semibold">{staff.name}</div>
            <div class="font-light tabular-nums tracking-tight">{staff.number}</div>
            <div>{staff.rank.name || "-"} | {staff.position.name || "-"}</div>
          </div>
        </Button>
      {/each}
    {/if}
  </div>
  <div bind:this={sentinel} class="h-1">
  </div>
</main>
<DialogDetail
  open={detailDialog.state}
  setOpen={detailDialog.setOpen}
  select={selected?.id ?? 0}/>
